import { type ReactElement, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { presaleApi, type Presale } from '../../lib/presale-api';

type Tab = 'launches' | 'contributions';

export function MyPresalesPage(): ReactElement {
  const [tab, setTab] = useState<Tab>('launches');
  const [launches, setLaunches] = useState<Presale[]>([]);
  const [contributions, setContributions] = useState<Presale[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      presaleApi
        .list()
        .then(setLaunches)
        .catch(() => {}),
      presaleApi
        .list()
        .then(setContributions)
        .catch(() => {}), // NOTE: Forge API does not yet support filtering by buyer address
    ]).finally(() => setLoading(false));
  }, []);

  const items = tab === 'launches' ? launches : contributions;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-white">My Presales</h1>

      {/* Tabs */}
      <div className="flex gap-2">
        {(
          [
            ['launches', 'My Launches'],
            ['contributions', 'My Contributions'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              tab === key
                ? 'border border-[#c4f70e]/30 bg-[#c4f70e]/10 text-[#c4f70e]'
                : 'border border-white/[0.06] bg-white/[0.02] text-white/40 hover:text-white/60'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-lg border border-white/[0.06] bg-white/[0.02]"
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center py-16 text-center">
          <p className="mb-4 text-white/40">
            {tab === 'launches'
              ? "You haven't created any presales yet."
              : "You haven't contributed to any presales yet."}
          </p>
          <Link
            to={tab === 'launches' ? '/presale/create' : '/presale'}
            className="text-sm text-[#c4f70e] hover:underline"
          >
            {tab === 'launches' ? 'Create your first presale' : 'Browse open presales'}
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((presale) => (
            <div
              key={presale.id}
              className="flex items-center justify-between gap-4 rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 transition-colors hover:border-[#c4f70e]/20"
            >
              <Link to={`/presale/${presale.id}`} className="min-w-0 flex-1">
                <div className="flex items-center gap-3">
                  {presale.imageUrl ? (
                    <img src={presale.imageUrl} alt="" className="h-10 w-10 rounded-full" />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#c4f70e]/10 font-bold text-[#c4f70e]">
                      {presale.tokenSymbol.charAt(0)}
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-semibold text-white">
                      {presale.tokenName}{' '}
                      <span className="font-mono text-white/30">${presale.tokenSymbol}</span>
                    </p>
                    <p className="text-xs text-white/30">
                      {Number(presale.totalRaised).toFixed(2)} /{' '}
                      {Number(presale.targetSol).toFixed(2)} SOL • {presale.buyerCount} buyers
                    </p>
                  </div>
                </div>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs ${
                    presale.status === 'OPEN'
                      ? 'bg-[#c4f70e]/10 text-[#c4f70e]'
                      : presale.status === 'LIVE'
                        ? 'bg-green-500/10 text-green-400'
                        : presale.status === 'FILLED'
                          ? 'bg-yellow-500/10 text-yellow-400'
                          : 'bg-white/10 text-white/40'
                  }`}
                >
                  {presale.status}
                </span>
              </Link>
              {tab === 'launches' && (
                <Link
                  to={`/presale/${presale.id}/manage`}
                  className="shrink-0 rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-xs text-white/60 transition-colors hover:border-[#c4f70e]/30 hover:text-[#c4f70e]"
                >
                  Manage
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
